/**
 * Plugin Manager
 *
 * Manages plugin instances for a single grid.
 * Each grid has its own PluginManager with its own set of plugin instances.
 *
 * **Plugin Order Matters**: By default, plugins are executed in the order they appear
 * in the `plugins` array. Plugins can override execution order for specific hooks via
 * `manifest.hookPriority` (lower values run earlier, default is 0). Plugins with equal
 * priority preserve their original array order.
 */

import { PLUGIN_ALIAS_COLLAPSE, PLUGIN_EVENT_ERROR, errorDiagnostic, warnDiagnostic } from '../internal/diagnostics';
import { validatePluginDependencies } from '../internal/validate-config';
import type { ColumnConfig, GridConfig } from '../types';
import type {
  AfterCellRenderContext,
  AfterRowRenderContext,
  BaseGridPlugin,
  CellClickEvent,
  CellEditor,
  CellMouseEvent,
  CellRenderer,
  GridElement,
  HeaderClickEvent,
  HeaderRenderer,
  HookName,
  PluginQuery,
  RowClickEvent,
  ScrollEvent,
} from './base-plugin';

/**
 * Insert a provided instance of every missing dependency that declares
 * `provide`, right before the first plugin that needs it. Provided instances
 * are expanded recursively, so their own provided dependencies precede them.
 */
function provideDependencies(
  plugins: BaseGridPlugin[],
  gate: (plugin: BaseGridPlugin) => BaseGridPlugin,
): BaseGridPlugin[] {
  // Also the cycle guard: a name is claimed before its provider is expanded.
  const names = new Set(plugins.map((p) => p.name));
  const result: BaseGridPlugin[] = [];
  const add = (plugin: BaseGridPlugin): void => {
    for (const dep of (plugin.constructor as typeof BaseGridPlugin).dependencies ?? []) {
      if (!dep.provide || names.has(dep.name) || (dep.when && !dep.when(plugin.resolvedConfig))) continue;
      names.add(dep.name);
      add(gate(dep.provide()));
    }
    result.push(plugin);
  };
  for (const plugin of plugins) add(plugin);
  return result;
}

/**
 * Manages plugins for a single grid instance.
 *
 * Plugins are executed in array order by default. Use `manifest.hookPriority`
 * to override execution order for specific hooks.
 */
export class PluginManager {
  // #region Properties
  /** Plugin instances in order of attachment */
  private plugins: BaseGridPlugin[] = [];

  /** Get all registered plugins (read-only) */
  getPlugins(): readonly BaseGridPlugin[] {
    return this.plugins;
  }

  /** Map from plugin class to instance for fast lookup */
  private pluginMap: Map<new (...args: unknown[]) => BaseGridPlugin, BaseGridPlugin> = new Map();

  /** Cell renderers registered by plugins */
  private cellRenderers: Map<string, CellRenderer> = new Map();

  /** Header renderers registered by plugins */
  private headerRenderers: Map<string, HeaderRenderer> = new Map();

  /** Cell editors registered by plugins */
  private cellEditors: Map<string, CellEditor> = new Map();

  /** Cached hook presence flags — invalidated on plugin attach/detach */
  private _hasAfterCellRender = false;
  private _hasAfterRowRender = false;
  /** Whether any plugin has a processRows hook. */
  _hasProcessRows = false;
  /** Whether any plugin with `modifiesRowStructure` is attached. Exposed for the sorting module. */
  _hasRowStructurePlugins = false;

  /** Cached plugin lists sorted by hookPriority per hook — invalidated on plugin attach/detach */
  #sortedPluginsByHook = new Map<HookName, BaseGridPlugin[]>();
  // #endregion

  // #region Event Bus State
  /**
   * Event listeners indexed by event type.
   * Maps event type → Map<plugin instance → callback>.
   * Using plugin instance as key enables auto-cleanup on detach.
   */
  private eventListeners: Map<string, Map<BaseGridPlugin, (detail: unknown) => void>> = new Map();
  // #endregion

  // #region Query System State
  /**
   * Query handlers indexed by query type.
   * Maps query type → Set of plugin instances that declare handling it.
   * Built from manifest.queries during plugin attach.
   */
  private queryHandlers: Map<string, Set<BaseGridPlugin>> = new Map();
  // #endregion

  // #region Lifecycle
  constructor(private grid: GridElement) {}

  /**
   * Attach all plugins from the config.
   *
   * Runs an alias-collapse pre-pass before per-instance attach: when multiple
   * plugin instances resolve to the same canonical constructor (e.g. the same
   * plugin passed twice, or once under an alias name), only the first instance
   * is attached, and the others' user configs are folded into it via
   * `BaseGridPlugin.mergeConfigsFrom()`.
   *
   * Skipped duplicates trigger a one-time `console.warn` with diagnostic code
   * `TBW023` (suppressed in production builds via `import.meta.env.PROD`).
   *
   * Missing dependencies that declare `provide` are created first and attached
   * before their dependents. `gate` maps each provided instance to the
   * canonical per-grid instance so it survives plugin re-initialization.
   */
  attachAll(plugins: BaseGridPlugin[], gate: (plugin: BaseGridPlugin) => BaseGridPlugin = (p) => p): void {
    const collapsed = this.#collapseAliasDuplicates(provideDependencies(plugins, gate));
    for (const plugin of collapsed) {
      this.attach(plugin);
    }
  }

  /**
   * Group plugin instances by canonical constructor. For each group with >1
   * instance, merge the trailing instances' user configs into the leading
   * instance and warn the developer once.
   *
   * Constructor identity is used as the canonical key — two class references
   * that point to the same constructor collapse cleanly without any name
   * table.
   */
  #collapseAliasDuplicates(plugins: BaseGridPlugin[]): BaseGridPlugin[] {
    if (plugins.length < 2) return plugins;
    const seen = new Map<unknown, BaseGridPlugin>();
    const seenDuplicates = new Map<BaseGridPlugin, BaseGridPlugin[]>();
    const result: BaseGridPlugin[] = [];
    for (const plugin of plugins) {
      const key = plugin.constructor;
      const existing = seen.get(key);
      if (!existing) {
        seen.set(key, plugin);
        result.push(plugin);
        continue;
      }
      // Duplicate — track for merge after the loop
      const dupes = seenDuplicates.get(existing) ?? [];
      dupes.push(plugin);
      seenDuplicates.set(existing, dupes);
    }
    if (seenDuplicates.size === 0) return result;
    for (const [first, dupes] of seenDuplicates) {
      // Merge configs (may throw on conflict — propagate up to the caller)
      first.mergeConfigsFrom(dupes);
      // Dev-time hint (skipped in production)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const env = (import.meta as any).env;
      if (env?.PROD) continue;
      const aliases = dupes.map((d) => d.name);
      warnDiagnostic(
        PLUGIN_ALIAS_COLLAPSE,
        `Multiple instances of plugin "${first.name}" detected (${[first.name, ...aliases].join(', ')}). They resolve to the same class — merging configs and attaching once. Remove the duplicate instantiation.`,
        this.grid.getAttribute('id') ?? undefined,
        first.name,
      );
    }
    return result;
  }

  /**
   * Attach a plugin to this grid.
   * Validates dependencies and notifies other plugins of the new attachment.
   */
  attach(plugin: BaseGridPlugin): void {
    // Validate plugin dependencies BEFORE attaching
    // This throws if a required dependency is missing
    validatePluginDependencies(
      plugin,
      this.plugins,
      this.grid.getAttribute('id') ?? undefined,
      this.grid.effectiveConfig ?? this.grid.gridConfig,
    );

    // Store by constructor for type-safe lookup
    this.pluginMap.set(plugin.constructor as new (...args: unknown[]) => BaseGridPlugin, plugin);
    this.plugins.push(plugin);

    // Register renderers/editors
    if (plugin.cellRenderers) {
      for (const [type, renderer] of Object.entries(plugin.cellRenderers)) {
        this.cellRenderers.set(type, renderer);
      }
    }
    if (plugin.headerRenderers) {
      for (const [type, renderer] of Object.entries(plugin.headerRenderers)) {
        this.headerRenderers.set(type, renderer);
      }
    }
    if (plugin.cellEditors) {
      for (const [type, editor] of Object.entries(plugin.cellEditors)) {
        this.cellEditors.set(type, editor);
      }
    }

    // Register query handlers from manifest
    this.registerQueryHandlers(plugin);

    // Call attach lifecycle method
    plugin.attach(this.grid);

    // Invalidate hook caches
    this.#invalidateHookCaches();

    // Notify existing plugins of the new attachment
    for (const existingPlugin of this.plugins) {
      if (existingPlugin !== plugin && existingPlugin.onPluginAttached) {
        existingPlugin.onPluginAttached(plugin.name, plugin);
      }
    }
  }

  /**
   * Register query handlers from a plugin's manifest.
   */
  private registerQueryHandlers(plugin: BaseGridPlugin): void {
    const PluginClass = plugin.constructor as typeof BaseGridPlugin;
    const manifest = PluginClass.manifest;
    if (!manifest?.queries) return;

    for (const queryDef of manifest.queries) {
      let handlers = this.queryHandlers.get(queryDef.type);
      if (!handlers) {
        handlers = new Set();
        this.queryHandlers.set(queryDef.type, handlers);
      }
      handlers.add(plugin);
    }
  }

  /**
   * Unregister query handlers for a plugin.
   */
  private unregisterQueryHandlers(plugin: BaseGridPlugin): void {
    for (const [queryType, handlers] of this.queryHandlers) {
      handlers.delete(plugin);
      if (handlers.size === 0) {
        this.queryHandlers.delete(queryType);
      }
    }
  }

  /**
   * Detach all plugins and clean up.
   * Notifies plugins of detachment via onPluginDetached hook.
   */
  detachAll(): void {
    // Notify all plugins before detaching (in forward order)
    for (const plugin of this.plugins) {
      for (const otherPlugin of this.plugins) {
        if (otherPlugin !== plugin && otherPlugin.onPluginDetached) {
          otherPlugin.onPluginDetached(plugin.name);
        }
      }
    }

    // Detach in reverse order
    for (let i = this.plugins.length - 1; i >= 0; i--) {
      const plugin = this.plugins[i];
      this.unsubscribeAll(plugin); // Clean up event subscriptions
      this.unregisterQueryHandlers(plugin); // Clean up query handlers
      plugin.detach();
      // Guarantee the `disconnectSignal` contract even when an override skips super.detach().
      plugin.__releaseDisconnectSignal();
    }
    this.plugins = [];
    this.pluginMap.clear();
    this.cellRenderers.clear();
    this.headerRenderers.clear();
    this.cellEditors.clear();
    this.eventListeners.clear();
    this.queryHandlers.clear();
    this._hasAfterCellRender = false;
    this._hasAfterRowRender = false;
    this._hasProcessRows = false;
    this.#sortedPluginsByHook.clear();
  }
  // #endregion

  // #region Plugin Lookup
  /**
   * Get a plugin instance by its class.
   */
  getPlugin<T extends BaseGridPlugin>(PluginClass: new (...args: any[]) => T): T | undefined {
    return this.pluginMap.get(PluginClass) as T | undefined;
  }

  /**
   * Get a plugin instance by its name.
   */
  getPluginByName(name: string): BaseGridPlugin | undefined {
    return this.plugins.find((p) => p.name === name || p.aliases?.includes(name));
  }

  /**
   * Check if a plugin is attached.
   */
  hasPlugin<T extends BaseGridPlugin>(PluginClass: new (...args: any[]) => T): boolean {
    return this.pluginMap.has(PluginClass);
  }

  /**
   * Get all attached plugins.
   */
  getAll(): readonly BaseGridPlugin[] {
    return this.plugins;
  }

  /**
   * Get names of all registered plugins (for debugging).
   */
  getRegisteredPluginNames(): string[] {
    return this.plugins.map((p) => p.name);
  }
  // #endregion

  // #region Renderers & Styles
  /**
   * Get a cell renderer by type name.
   */
  getCellRenderer(type: string): CellRenderer | undefined {
    return this.cellRenderers.get(type);
  }

  /**
   * Get a header renderer by type name.
   */
  getHeaderRenderer(type: string): HeaderRenderer | undefined {
    return this.headerRenderers.get(type);
  }

  /**
   * Get a cell editor by type name.
   */
  getCellEditor(type: string): CellEditor | undefined {
    return this.cellEditors.get(type);
  }

  /**
   * Get all CSS styles from all plugins as structured data.
   * Returns an array of { name, styles } for each plugin with styles.
   */
  getPluginStyles(): Array<{ name: string; styles: string }> {
    return this.plugins.filter((p) => p.styles).map((p) => ({ name: p.name, styles: p.styles! }));
  }
  // #endregion

  // #region Hook Execution

  /**
   * Execute processRows hook on all plugins.
   * Returns a mutable copy only when at least one plugin transforms rows.
   */
  processRows(rows: readonly any[]): any[] {
    if (!this._hasProcessRows) return rows as any[];
    let result: any[] = rows as any[];
    for (const plugin of this.#getPluginsForHook('processRows')) {
      const transformed = plugin.processRows!(result);
      if (transformed !== result) {
        result = transformed;
      }
    }
    return result;
  }

  /**
   * Execute the internal orderColumns hook on all plugins (full column list, hidden included).
   * Returns the input array unchanged when no plugin reorders.
   */
  orderColumns<C extends ColumnConfig>(columns: readonly C[]): readonly C[] {
    let result = columns;
    for (const plugin of this.plugins) {
      if (plugin.orderColumns) result = plugin.orderColumns(result) as C[];
    }
    return result;
  }

  /**
   * Execute processColumns hook on all plugins.
   */
  processColumns(columns: readonly ColumnConfig[]): ColumnConfig[] {
    let result = [...columns];
    for (const plugin of this.#getPluginsForHook('processColumns')) {
      result = plugin.processColumns!(result);
    }
    return result;
  }

  /**
   * Execute processConfig hook on all plugins.
   * Lets plugins contribute to the effective config during each merge. Plugins
   * mutate the passed config in place (e.g. the shell plugin folds its state
   * into `config.shell`).
   */
  processConfig(config: GridConfig): void {
    for (const plugin of this.#getPluginsForHook('processConfig')) {
      plugin.processConfig!(config);
    }
  }

  /**
   * Execute beforeRender hook on all plugins.
   */
  beforeRender(): void {
    for (const plugin of this.plugins) {
      plugin.beforeRender?.();
    }
  }

  /**
   * Execute afterStructuralRender hook on all plugins.
   * Fired synchronously by the grid right after a full structural DOM rebuild,
   * before paint. Used by plugins that wrap/relocate the freshly built grid DOM.
   */
  afterStructuralRender(): void {
    for (const plugin of this.#getPluginsForHook('afterStructuralRender')) {
      plugin.afterStructuralRender!();
    }
  }

  /**
   * Execute beforeStructuralRender hook on all plugins. Fired synchronously by
   * the grid right before it tears down and rebuilds its root DOM, so plugins
   * can move relocated light-DOM nodes back to their original container.
   */
  beforeStructuralRender(): void {
    for (const plugin of this.plugins) {
      plugin.beforeStructuralRender?.();
    }
  }

  /**
   * Let a plugin (re-)parse its own light-DOM contributions into plugin state.
   * Generic seam — core holds no plugin-specific light-DOM tag knowledge.
   */
  parseLightDom(): void {
    for (const plugin of this.plugins) {
      plugin.parseLightDom?.();
    }
  }

  /**
   * Ask each plugin to re-parse its light-DOM contributions AND refresh any
   * already-rendered chrome in place (e.g. a shell title/tool buttons that a
   * framework projected asynchronously). Called from `refreshColumns` and the
   * light-DOM observer — generic seam, core holds no plugin-specific knowledge.
   */
  syncLightDom(): void {
    for (const plugin of this.plugins) {
      plugin.syncLightDom?.();
    }
  }

  /**
   * Ask all plugins whether the most recent config change requires a full
   * structural re-render. Returns `true` if ANY plugin votes for one.
   */
  needsStructuralRerender(): boolean {
    let needed = false;
    for (const plugin of this.plugins) {
      // Don't short-circuit: a voting plugin may read DOM signatures with side
      // effects in future, but today this is pure — still, evaluate all so a
      // single early `true` doesn't hide a later plugin's intent.
      if (plugin.needsStructuralRerender?.()) needed = true;
    }
    return needed;
  }

  /**
   * Execute afterRender hook on all plugins.
   */
  afterRender(): void {
    for (const plugin of this.#getPluginsForHook('afterRender')) {
      plugin.afterRender!();
    }
  }

  /**
   * Execute afterCellRender hook on all plugins for a single cell.
   * Called during cell rendering for efficient cell-level modifications.
   *
   * @param context - The cell render context
   */
  afterCellRender(context: AfterCellRenderContext): void {
    for (const plugin of this.#getPluginsForHook('afterCellRender')) {
      plugin.afterCellRender!(context);
    }
  }

  /**
   * Check if any plugin has the afterCellRender hook implemented.
   * Cached — invalidated on plugin attach/detach.
   */
  hasAfterCellRenderHook(): boolean {
    return this._hasAfterCellRender;
  }

  /**
   * Execute afterRowRender hook on all plugins for a single row.
   * Called after all cells in a row are rendered for efficient row-level modifications.
   *
   * @param context - The row render context
   */
  afterRowRender(context: AfterRowRenderContext): void {
    for (const plugin of this.#getPluginsForHook('afterRowRender')) {
      plugin.afterRowRender!(context);
    }
  }

  /**
   * Check if any plugin has the afterRowRender hook implemented.
   * Cached — invalidated on plugin attach/detach.
   */
  hasAfterRowRenderHook(): boolean {
    return this._hasAfterRowRender;
  }

  /** Recompute cached hook presence flags and clear sorted-plugin caches. */
  #invalidateHookCaches(): void {
    this._hasAfterCellRender = this.plugins.some((p) => typeof p.afterCellRender === 'function');
    this._hasAfterRowRender = this.plugins.some((p) => typeof p.afterRowRender === 'function');
    this._hasProcessRows = this.plugins.some((p) => typeof p.processRows === 'function');
    this._hasRowStructurePlugins = this.plugins.some(
      (p) => (p.constructor as typeof BaseGridPlugin).manifest?.modifiesRowStructure === true,
    );
    this.#sortedPluginsByHook.clear();
  }

  /**
   * Get plugins that implement a given hook, sorted by hookPriority.
   * Results are cached and invalidated on plugin attach/detach.
   *
   * Plugins without a declared priority default to 0.
   * Lower values execute earlier; higher values execute later.
   * Plugins with equal priority preserve their original array order (stable sort).
   */
  #getPluginsForHook(hookName: HookName): BaseGridPlugin[] {
    let sorted = this.#sortedPluginsByHook.get(hookName);
    if (sorted) return sorted;

    sorted = this.plugins.filter((p) => hookName in p && typeof p[hookName] === 'function');

    // Only sort when at least one plugin declares a priority for this hook
    const getPriority = (p: BaseGridPlugin): number => {
      const hp = (p.constructor as typeof BaseGridPlugin).manifest?.hookPriority;
      if (!hp) return 0;
      const val = hp[hookName as keyof typeof hp];
      return val ?? 0;
    };
    const needsSort = sorted.some((p) => getPriority(p) !== 0);

    if (needsSort) {
      sorted.sort((a, b) => getPriority(a) - getPriority(b));
    }

    this.#sortedPluginsByHook.set(hookName, sorted);
    return sorted;
  }

  /**
   * Execute onScrollRender hook on all plugins.
   * Called after scroll-triggered row rendering for lightweight visual state updates.
   */
  onScrollRender(): void {
    for (const plugin of this.#getPluginsForHook('onScrollRender')) {
      plugin.onScrollRender!();
    }
  }

  /**
   * Get the height of a specific row from plugins.
   * Used for synthetic rows (group headers, etc.) that have fixed heights.
   * Returns undefined if no plugin provides a height for this row.
   */
  getRowHeight(row: unknown, index: number): number | undefined {
    for (const plugin of this.plugins) {
      if (typeof plugin.getRowHeight === 'function') {
        const height = plugin.getRowHeight(row, index);
        if (height !== undefined) {
          return height;
        }
      }
    }
    return undefined;
  }

  /**
   * Check if any plugin implements the getRowHeight() hook.
   * When true, the grid should use variable heights mode with position caching.
   */
  hasRowHeightPlugin(): boolean {
    for (const plugin of this.plugins) {
      if (typeof plugin.getRowHeight === 'function') {
        return true;
      }
    }
    return false;
  }

  /**
   * Adjust the virtualization start index based on plugin needs.
   * Returns the minimum start index from all plugins.
   */
  adjustVirtualStart(start: number, scrollTop: number, rowHeight: number): number {
    let adjustedStart = start;
    for (const plugin of this.plugins) {
      if (typeof plugin.adjustVirtualStart === 'function') {
        const pluginStart = plugin.adjustVirtualStart(start, scrollTop, rowHeight);
        if (pluginStart < adjustedStart) {
          adjustedStart = pluginStart;
        }
      }
    }
    return adjustedStart;
  }

  /**
   * Execute renderRow hook on all plugins, in `hookPriority` order.
   * Returns true if any plugin handled the row.
   */
  renderRow(row: any, rowEl: HTMLElement, rowIndex: number): boolean {
    for (const plugin of this.#getPluginsForHook('renderRow')) {
      if (plugin.renderRow?.(row, rowEl, rowIndex)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Query all plugins with a generic query and collect responses.
   * This enables inter-plugin communication without the core knowing plugin-specific concepts.
   *
   * Uses manifest-based routing when available: only plugins that declare handling
   * the query type in their `manifest.queries` are invoked. Falls back to querying
   * all plugins for backwards compatibility with legacy plugins.
   *
   * @param query - The query object containing type and context
   * @returns Array of non-undefined responses from plugins
   */
  queryPlugins<T>(query: PluginQuery): T[] {
    const responses: T[] = [];

    // Try manifest-based routing first
    const handlers = this.queryHandlers.get(query.type);
    if (handlers && handlers.size > 0) {
      // Route only to plugins that declared this query type
      for (const plugin of handlers) {
        const response = plugin.handleQuery?.(query);
        if (response !== undefined) {
          responses.push(response as T);
        }
      }
      return responses;
    }

    // Fallback: query all plugins (legacy behavior for plugins without manifest)
    for (const plugin of this.plugins) {
      const response = plugin.handleQuery?.(query);
      if (response !== undefined) {
        responses.push(response as T);
      }
    }
    return responses;
  }
  // #endregion

  // #region Event Bus
  /**
   * Subscribe a plugin to an event type.
   * The subscription is automatically cleaned up when the plugin is detached.
   *
   * @param plugin - The subscribing plugin instance
   * @param eventType - The event type to listen for
   * @param callback - The callback to invoke when the event is emitted
   */
  subscribe(plugin: BaseGridPlugin, eventType: string, callback: (detail: unknown) => void): void {
    let listeners = this.eventListeners.get(eventType);
    if (!listeners) {
      listeners = new Map();
      this.eventListeners.set(eventType, listeners);
    }
    listeners.set(plugin, callback);
  }

  /**
   * Unsubscribe a plugin from an event type.
   *
   * @param plugin - The subscribing plugin instance
   * @param eventType - The event type to stop listening for
   */
  unsubscribe(plugin: BaseGridPlugin, eventType: string): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.delete(plugin);
      if (listeners.size === 0) {
        this.eventListeners.delete(eventType);
      }
    }
  }

  /**
   * Unsubscribe a plugin from all events.
   * Called automatically when a plugin is detached.
   *
   * @param plugin - The plugin to unsubscribe
   */
  unsubscribeAll(plugin: BaseGridPlugin): void {
    for (const [eventType, listeners] of this.eventListeners) {
      listeners.delete(plugin);
      if (listeners.size === 0) {
        this.eventListeners.delete(eventType);
      }
    }
  }

  /**
   * Emit an event to all subscribed plugins.
   * This is for inter-plugin communication only; it does not dispatch DOM events.
   *
   * @param eventType - The event type to emit
   * @param detail - The event payload
   */
  emitPluginEvent<T>(eventType: string, detail: T): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      for (const callback of listeners.values()) {
        try {
          callback(detail);
        } catch (error) {
          errorDiagnostic(
            PLUGIN_EVENT_ERROR,
            `Error in plugin event handler for "${eventType}": ${error}`,
            this.grid.getAttribute('id') ?? undefined,
          );
        }
      }
    }
  }
  // #endregion

  // #region Event Hooks
  /**
   * Execute onKeyDown hook on all plugins.
   * Returns true if any plugin handled the event.
   */
  onKeyDown(event: KeyboardEvent): boolean {
    for (const plugin of this.#getPluginsForHook('onKeyDown')) {
      if (plugin.onKeyDown!(event)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Execute onCellClick hook on all plugins.
   * Returns true if any plugin handled the event.
   */
  onCellClick(event: CellClickEvent): boolean {
    for (const plugin of this.#getPluginsForHook('onCellClick')) {
      if (plugin.onCellClick!(event)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Execute onRowClick hook on all plugins.
   * Returns true if any plugin handled the event.
   */
  onRowClick(event: RowClickEvent): boolean {
    for (const plugin of this.#getPluginsForHook('onRowClick')) {
      if (plugin.onRowClick!(event)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Execute onHeaderClick hook on all plugins.
   * Returns true if any plugin handled the event.
   */
  onHeaderClick(event: HeaderClickEvent): boolean {
    for (const plugin of this.#getPluginsForHook('onHeaderClick')) {
      if (plugin.onHeaderClick!(event)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Execute onScroll hook on all plugins.
   */
  onScroll(event: ScrollEvent): void {
    for (const plugin of this.#getPluginsForHook('onScroll')) {
      plugin.onScroll!(event);
    }
  }

  /**
   * Execute onCellMouseDown hook on all plugins.
   * Returns true if any plugin handled the event.
   */
  onCellMouseDown(event: CellMouseEvent): boolean {
    for (const plugin of this.#getPluginsForHook('onCellMouseDown')) {
      if (plugin.onCellMouseDown!(event)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Execute onCellMouseMove hook on all plugins.
   * Returns true if any plugin handled the event.
   */
  onCellMouseMove(event: CellMouseEvent): boolean {
    for (const plugin of this.#getPluginsForHook('onCellMouseMove')) {
      if (plugin.onCellMouseMove!(event)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Execute onCellMouseUp hook on all plugins.
   * Returns true if any plugin handled the event.
   */
  onCellMouseUp(event: CellMouseEvent): boolean {
    for (const plugin of this.#getPluginsForHook('onCellMouseUp')) {
      if (plugin.onCellMouseUp!(event)) {
        return true;
      }
    }
    return false;
  }

  // #endregion

  // #region Scroll Boundary Hooks

  /**
   * Collect horizontal scroll boundary offsets from all plugins.
   * Combines offsets from all plugins that report them.
   *
   * @param rowEl - The row element (optional, for calculating widths from rendered cells)
   * @param focusedCell - The currently focused cell element (optional, to determine if scrolling should be skipped)
   * @returns Combined left and right pixel offsets, plus skipScroll if any plugin requests it
   */
  getHorizontalScrollOffsets(
    rowEl?: HTMLElement,
    focusedCell?: HTMLElement,
  ): { left: number; right: number; skipScroll?: boolean } {
    let left = 0;
    let right = 0;
    let skipScroll = false;
    for (const plugin of this.plugins) {
      const offsets = plugin.getHorizontalScrollOffsets?.(rowEl, focusedCell);
      if (offsets) {
        left += offsets.left;
        right += offsets.right;
        if (offsets.skipScroll) {
          skipScroll = true;
        }
      }
    }
    return { left, right, skipScroll };
  }

  /**
   * Collect vertical scroll boundary offsets from all plugins.
   * Combines offsets from every plugin that overlays the rows viewport.
   *
   * @param focusedRowIndex - Index of the row being scrolled into view (optional, lets a plugin report that the row needs no scrolling)
   * @returns Combined top and bottom pixel offsets, plus skipScroll if any plugin requests it
   * @since 3.6.0
   */
  getVerticalScrollOffsets(focusedRowIndex?: number): { top: number; bottom: number; skipScroll?: boolean } {
    let top = 0;
    let bottom = 0;
    let skipScroll = false;
    for (const plugin of this.plugins) {
      const offsets = plugin.getVerticalScrollOffsets?.(focusedRowIndex);
      if (offsets) {
        top += offsets.top;
        bottom += offsets.bottom;
        if (offsets.skipScroll) {
          skipScroll = true;
        }
      }
    }
    return { top, bottom, skipScroll };
  }
  // #endregion

  // #region Shell Integration Hooks

  /**
   * Collect tool panels from all plugins.
   * Returns panels sorted by order (ascending).
   */
  getToolPanels(): {
    plugin: BaseGridPlugin;
    panel: NonNullable<ReturnType<NonNullable<BaseGridPlugin['getToolPanel']>>>;
  }[] {
    const panels: {
      plugin: BaseGridPlugin;
      panel: NonNullable<ReturnType<NonNullable<BaseGridPlugin['getToolPanel']>>>;
    }[] = [];
    for (const plugin of this.plugins) {
      const panel = plugin.getToolPanel?.();
      if (panel) {
        panels.push({ plugin, panel });
      }
    }
    // Sort by order (ascending), default to 0
    return panels.sort((a, b) => (a.panel.order ?? 0) - (b.panel.order ?? 0));
  }

  /**
   * Collect header contents from all plugins.
   * Returns contents sorted by order (ascending).
   */
  getHeaderContents(): {
    plugin: BaseGridPlugin;
    content: NonNullable<ReturnType<NonNullable<BaseGridPlugin['getHeaderContent']>>>;
  }[] {
    const contents: {
      plugin: BaseGridPlugin;
      content: NonNullable<ReturnType<NonNullable<BaseGridPlugin['getHeaderContent']>>>;
    }[] = [];
    for (const plugin of this.plugins) {
      const content = plugin.getHeaderContent?.();
      if (content) {
        contents.push({ plugin, content });
      }
    }
    // Sort by order (ascending), default to 0
    return contents.sort((a, b) => (a.content.order ?? 0) - (b.content.order ?? 0));
  }
  // #endregion
}
